// Workspace → Notifications — the full notification list.
//
// This was the Inbox screen, which replaced a Placeholder whose own blurb
// promised "Notifications and the approvals queue", so the bell was the only
// place a notification ever appeared and anything past the bell's 20 was
// unreachable. This is the notifications half; the approvals queue still lives
// on its own screens (leave on /hr, bug reports on /org) and the links here go
// there rather than duplicating them.
import { useEffect, useId, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useNotifications, type Notif } from '../lib/useNotifications';
import { addDays, fmtDayKey, fmtRelative, isoDay } from '../lib/dates';
import { useBelow, useMediaQuery } from '../lib/useMediaQuery';
import { kindIcon, kindLabel, notificationRoute } from '../lib/notificationKinds';
import { Button, Icon, IconButton, TONE } from './primitives';
import { Card, Eyebrow } from './card';
import { EmptyState, LoadError, useToast } from './feedback';

// A notification's time is the viewer's, not the org's (UI-CONVENTIONS §2, "Not
// a date bug"), so the day it is filed under is the browser's calendar day.
const dayHeading = (key: string, now: Date): string => {
  if (key === isoDay(now)) return 'Today';
  if (key === isoDay(addDays(now, -1))) return 'Yesterday';
  const year = key.slice(0, 4);
  const label = fmtDayKey(key, { weekday: true });
  return year === String(now.getFullYear()) ? label : `${label} ${year}`;
};

/** Newest first, as they arrive, cut into runs of one calendar day. */
const byDay = (items: readonly Notif[]): { key: string; rows: Notif[] }[] => {
  const days: { key: string; rows: Notif[] }[] = [];
  for (const n of items) {
    const key = isoDay(new Date(n.created_at));
    const last = days[days.length - 1];
    if (last?.key === key) last.rows.push(n);
    else days.push({ key, rows: [n] });
  }
  return days;
};

export function NotificationsTab({ onUnreadChange }: { onUnreadChange: () => void }) {
  const { session } = useAuth();
  const toast = useToast();
  // 200, not the bell's 20 — this is the tab you come to precisely because
  // something has scrolled off the bell.
  const { items, loading, error, unread, markRead, markAllRead, dismiss, clearRead, reload } =
    useNotifications(session?.user.id, 200);
  const readCount = items.filter((n) => n.read_at).length;
  // Asked once here rather than by each of up to 200 rows.
  const phone = useBelow('md');
  const coarse = useMediaQuery('(pointer: coarse)');

  // The unread count itself renders on the tab and the sidebar, from the
  // attention registry (docs/UI-CONVENTIONS.md §1) — not here. That registry only
  // refetches on navigation, so tell it when this list's unread moves (mark read,
  // dismiss, a realtime arrival) or both badges go stale together. Not for the
  // list's own first load: App counted on arrival at this screen already.
  //
  // Opening a row is the one write this effect cannot cover: the row is a link,
  // so the tab is usually gone before `unread` moves (and a calendar link stays
  // on /workspace, where nothing else recounts). That path refreshes the
  // registry itself, once its write has settled — see onOpen below.
  const lastUnread = useRef<number | null>(null);
  useEffect(() => {
    if (loading) return;
    if (lastUnread.current !== null && lastUnread.current !== unread) onUnreadChange();
    lastUnread.current = unread;
  }, [loading, unread, onUnreadChange]);

  // A write that fails puts the row back (the hook reloads); say why it came
  // back. Success is the row changing, except the bulk clear, which removes a
  // screenful at once and so says how many went.
  const run = async (write: Promise<string | null>, done?: string) => {
    const failed = await write;
    if (failed) toast.push({ tone: 'error', message: `That didn’t save — ${failed}` });
    else if (done) toast.push({ tone: 'success', message: done });
  };

  const now = new Date();

  return (
    <Card
      label="Notifications"
      right={
        unread > 0 && (
          <Button
            size="sm"
            variant="ghost"
            className="hf-hit"
            onClick={() => void run(markAllRead())}
          >
            Mark all read
          </Button>
        )
      }
    >
      {error && <LoadError what="your notifications" onRetry={() => void reload()} />}
      {loading ? (
        <div role="status" style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' }}>
          Loading…
        </div>
      ) : items.length === 0 ? (
        // After a failed read "nothing yet" would be a claim we can't make.
        !error && (
          <EmptyState title="No notifications yet">
            Leave requests and decisions, time corrections, compensated hours, bug reports and
            calendar events that concern you will appear here.
          </EmptyState>
        )
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--hf-space-4)',
            marginTop: error ? 'var(--hf-space-4)' : 0,
          }}
        >
          {byDay(items).map((day) => (
            <section key={day.key} aria-labelledby={`notif-day-${day.key}`}>
              <Eyebrow as="h3" id={`notif-day-${day.key}`}>
                {dayHeading(day.key, now)}
              </Eyebrow>
              <ul style={{ listStyle: 'none', margin: 'var(--hf-space-2) 0 0', padding: 0 }}>
                {day.rows.map((n) => (
                  <Row
                    key={n.id}
                    n={n}
                    phone={phone}
                    touch={phone || coarse}
                    onOpen={() => {
                      if (!n.read_at) void run(markRead(n.id)).then(onUnreadChange);
                    }}
                    onDismiss={() => void run(dismiss(n.id))}
                  />
                ))}
              </ul>
            </section>
          ))}
          {/* Apart from "Mark all read", not beside it: this one deletes
              (UI-CONVENTIONS §5). Read rows only, so it needs no confirm. */}
          {readCount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="sm"
                variant="ghost"
                className="hf-hit"
                onClick={() =>
                  void run(
                    clearRead(),
                    `Cleared ${readCount} read ${readCount === 1 ? 'notification' : 'notifications'}.`,
                  )
                }
              >
                Clear {readCount} read
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// Unread is said three ways, none of them colour alone (WS-06, XA-15): the dot's
// presence, a heavier body, and the word itself in the row's name — the dot was
// aria-hidden with no text, so a screen reader could not tell new from old.
//
// The link is the body and the line under it, so the whole text is the target:
// the body alone was a 20px strip on one line. Its name is still the body; the
// kind and time are its description.
//
// On a phone (below md) the text gets the row's width: the kind's icon goes
// (the line under the body names the kind), and the actions stack in a column
// at the right edge, 44px each, rather than sit side by side. At 320px two
// 32px buttons and the icon left the text a column 82px wide. Under any finger
// (`touch`, a tablet's too) the actions are 44px for real, side by side where
// there is room: two 32px buttons 8px apart had overlapping 44px areas, and a
// tap just right of the check dismissed instead.
function Row({
  n,
  phone,
  touch,
  onOpen,
  onDismiss,
}: {
  n: Notif;
  phone: boolean;
  touch: boolean;
  onOpen: () => void;
  onDismiss: () => void;
}) {
  const to = notificationRoute(n);
  const unread = !n.read_at;
  const id = useId();
  const text = (
    <>
      <span
        id={`${id}-body`}
        style={{ display: 'block', fontWeight: unread ? 'var(--hf-weight-medium)' : undefined }}
      >
        {unread && <span className="hf-sr-only">Unread:</span>} {n.body}
      </span>
      <span
        id={`${id}-meta`}
        style={{
          display: 'block',
          marginTop: 'var(--hf-space-1)',
          fontSize: 'var(--hf-text-xs)',
          color: 'var(--hf-ink-3)',
        }}
      >
        {kindLabel(n.kind)} · <time dateTime={n.created_at}>{fmtRelative(n.created_at)}</time>
      </span>
    </>
  );
  const size = touch ? 44 : 32;
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--hf-space-2)',
        padding: 'var(--hf-space-2)',
        borderTop: '1px solid var(--hf-rule)',
        background: unread ? 'var(--hf-surface-2)' : 'transparent',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 'var(--hf-space-2)',
          height: 'var(--hf-space-2)',
          marginTop: 'var(--hf-space-2)',
          borderRadius: 'var(--hf-radius-pill)',
          flexShrink: 0,
          // Amber, as the unread count is on the tab and the sidebar (§1).
          background: unread ? TONE.warning.fg : 'transparent',
        }}
      />
      {!phone && (
        <Icon
          name={kindIcon(n.kind)}
          size={14}
          style={{ flexShrink: 0, marginTop: 'var(--hf-space-1)', color: 'var(--hf-ink-3)' }}
        />
      )}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 'var(--hf-text-md)',
          lineHeight: 'var(--hf-leading-body)',
          overflowWrap: 'anywhere',
        }}
      >
        {to ? (
          // The whole link, query included (/workspace?tab=calendar&date=…).
          <Link
            to={to}
            onClick={onOpen}
            aria-labelledby={`${id}-body`}
            aria-describedby={`${id}-meta`}
            style={{ color: 'var(--hf-ink)', textDecoration: 'none', display: 'block' }}
          >
            {text}
          </Link>
        ) : (
          <div style={{ color: 'var(--hf-ink)' }}>{text}</div>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: phone ? 'column' : 'row',
          gap: phone ? 0 : 'var(--hf-space-2)',
          flexShrink: 0,
        }}
      >
        {/* Nowhere to open it, so opening can't read it: reading is its own act. */}
        {unread && !to && (
          <IconButton icon="check" label="Mark as read" size={size} onClick={onOpen} />
        )}
        <IconButton icon="x" label="Dismiss notification" size={size} onClick={onDismiss} />
      </div>
    </li>
  );
}
